import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { registerAffiliateInfo } from '@/lib/analytics'
import { createLogger } from '@/lib/logger'
import { isPopulatedObject } from '@/lib/utils'
import { cache } from 'react'
import { isDiscontinued } from '@/lib/app-categories/discontinued'
import type {
  App,
  Category,
  AppCategoryRecommendation,
  CategoryCriteriaCriterion,
  Criterion,
  Faq,
  Media,
  Link,
} from '@/payload-types'

const logger = createLogger('Formatting')

// Type guards to ensure docs arrays exclude numeric IDs (under-populated relations)
function isAcrObject(
  r: AppCategoryRecommendation | number | null | undefined,
): r is AppCategoryRecommendation {
  return (
    typeof r === 'object' &&
    r !== null &&
    !Array.isArray(r) &&
    'category' in (r as unknown as object)
  )
}

function isCccObject(
  r: CategoryCriteriaCriterion | number | null | undefined,
): r is CategoryCriteriaCriterion {
  return (
    typeof r === 'object' &&
    r !== null &&
    !Array.isArray(r) &&
    'criterion' in (r as unknown as object)
  )
}

function isCriterionObject(r: Criterion | number | null | undefined): r is Criterion {
  return (
    typeof r === 'object' && r !== null && !Array.isArray(r) && 'name' in (r as unknown as object)
  )
}

/**
 * Extract unique criteria from a single hydrated app.
 *
 * Assumes the app has categoryRecommendations -> category -> categoryCriteria -> criterion populated.
 * Gracefully handles under-populated data (e.g., category or criterion is an ID instead of object)
 * by skipping those entries. This allows the function to work correctly during transient states
 * when data is being refetched (e.g., after app swap in comparison pages).
 * Returns a de-duplicated list of Criterion objects with their associated categoryCriteria data and optional rating.
 *
 * @param app - The app object with populated categoryRecommendations
 * @param reviewCriterionRatings - Optional array of review criterion ratings to include rating values
 */
export function extractUniqueCriteriaFromApp(
  app: Partial<App>,
  reviewCriterionRatings?: Array<{
    categoryCriterion?: CategoryCriteriaCriterion['id'] | CategoryCriteriaCriterion | null
    rating?: number | null
    isApplicable?: boolean | null
  }>,
  categoryId?: Category['id'],
): Array<
  Criterion & {
    categoryCriteria?: CategoryCriteriaCriterion
    rating?: number | null
    isApplicable?: boolean
  }
> {
  const ids = new Set<Criterion['id']>()
  const list: Array<
    Criterion & {
      categoryCriteria?: CategoryCriteriaCriterion
      rating?: number | null
      isApplicable?: boolean
    }
  > = []

  // Build a map of categoryCriterion ID to { rating, isApplicable } for quick lookup
  const ratingMap = new Map<number, { rating: number | null; isApplicable?: boolean }>()
  if (reviewCriterionRatings) {
    reviewCriterionRatings.forEach((rcr) => {
      if (rcr.categoryCriterion) {
        const ccId =
          typeof rcr.categoryCriterion === 'number'
            ? rcr.categoryCriterion
            : rcr.categoryCriterion.id
        ratingMap.set(ccId, {
          rating: rcr.rating ?? null,
          isApplicable: rcr.isApplicable ?? undefined,
        })
      }
    })
  }

  const acrs = app.categoryRecommendations?.docs?.filter(isAcrObject) || []
  acrs.forEach((acr) => {
    // Skip if category relation is an ID instead of an object
    // This can happen transiently when apps are swapped and data is being refetched
    if (acr && acr.category && typeof acr.category !== 'object') {
      return
    }

    const category = (acr?.category as Category | null) ?? null
    if (!category) return

    // When categoryId filter is provided, skip non-matching categories
    if (categoryId !== undefined && category.id !== categoryId) return

    const ccs = category.categoryCriteria?.docs?.filter(isCccObject) || []
    ccs.forEach((ccc) => {
      // Skip if criterion relation is an ID instead of an object
      // This can happen transiently when apps are swapped and data is being refetched
      if (ccc && (ccc as any).criterion && typeof (ccc as any).criterion !== 'object') {
        return
      }

      const crit = (ccc?.criterion as Criterion | null) ?? null
      if (crit && !ids.has(crit.id)) {
        ids.add(crit.id)
        // Get the rating + isApplicable for this categoryCriterion if available
        const ratingData = ratingMap.get(ccc.id)
        list.push({
          ...crit,
          categoryCriteria: ccc,
          rating: ratingData?.rating,
          isApplicable: ratingData?.isApplicable,
        })
      }
    })
  })

  return list
}

/**
 * Extract unique criteria from an array of hydrated apps.
 *
 * Assumes each app is hydrated similarly to extractUniqueCriteriaFromApp. Logs warnings in non-production
 * if a nested relation is under-populated.
 * Returns a de-duplicated list of Criterion objects across all apps.
 */
export function extractUniqueCriteriaFromApps(apps: App[]): Criterion[] {
  const ids = new Set<Criterion['id']>()
  const list: Criterion[] = []

  apps.forEach((app) => {
    const acrs = app.categoryRecommendations?.docs?.filter(isAcrObject) || []
    acrs.forEach((acr) => {
      const category = (acr?.category as Category | null) ?? null
      if (!category) return

      const ccs = category.categoryCriteria?.docs?.filter(isCccObject) || []
      ccs.forEach((ccc) => {
        // Warn if criterion relation is an ID instead of an object
        if (ccc && (ccc as any).criterion && typeof (ccc as any).criterion !== 'object') {
          if (process.env.NODE_ENV !== 'production') {
            logger.error(
              '[criteria] Expected populated `cc.criterion` object but received ID. Increase query depth or add populate for `category.categoryCriteria.criterion`.',
              {
                appId: app.id,
                appSlug: (app as any)?.slug,
                categoryId: (category as any)?.id,
                ccId: (ccc as any)?.id,
                criterion: (ccc as any)?.criterion,
                stackTrace: new Error().stack,
              },
            )
          }
        }

        const crit = (ccc?.criterion as Criterion | null) ?? null
        if (crit && !ids.has(crit.id)) {
          ids.add(crit.id)
          list.push(crit)
        }
      })
    })
  })

  return list
}

export function extractBlogSlugUrlFromFaq(faq: Faq): string | null {
  const blog = faq.blog
  if (!blog) return null
  // Guard against under-populated relations (IDs) of any primitive type
  if (typeof blog !== 'object' || blog === null) {
    if (process.env.NODE_ENV !== 'production') {
      logger.error('[faq] Expected populated `faq.blog` object but received ID.', {
        faqId: (faq as any)?.id,
        blogValue: blog,
        stackTrace: new Error().stack,
      })
    }
    return null
  }
  const slug = (blog as any).slug
  return typeof slug === 'string' && slug ? `/blog/${slug}` : null
}

// CompareHero needs criteria from only the overlapping categories (shared by all compared apps)
export function extractCriteriaForCategories(
  apps: App[],
  categoryIds: Array<Category['id']>,
): Criterion[] {
  const categoryIdSet = new Set(categoryIds)
  const ids = new Set<Criterion['id']>()
  const list: Criterion[] = []

  apps.forEach((app) => {
    const acrs = app.categoryRecommendations?.docs?.filter(isAcrObject) || []
    acrs.forEach((acr) => {
      const category = (acr?.category as Category | null) ?? null
      if (!category || !categoryIdSet.has(category.id)) return

      const ccs = category.categoryCriteria?.docs?.filter(isCccObject) || []
      ccs.forEach((ccc) => {
        const crit = ccc?.criterion
        if (!isCriterionObject(crit)) {
          if (process.env.NODE_ENV !== 'production' && crit !== null && crit !== undefined) {
            logger.error(
              '[criteria] Expected populated `ccc.criterion` object but received ID. Increase query depth or add populate for `category.categoryCriteria.criterion`.',
              { appId: app.id, categoryId: category.id, criterion: crit },
            )
          }
          return
        }
        if (!ids.has(crit.id)) {
          ids.add(crit.id)
          list.push(crit)
        }
      })
    })
  })

  return list
}

/**
 * Extract unique criteria from an array of apps, filtered to a specific category.
 *
 * Used by CompareCriterias to show only criteria relevant to the current category
 * section (e.g., "CRM At a Glance" should only show CRM criteria, not Sales criteria).
 *
 * @param apps - Array of apps with populated categoryRecommendations
 * @param categoryId - The category ID to filter criteria by
 * @returns Deduplicated list of Criterion objects that belong to the specified category
 */
export function extractCriteriaForCategory(apps: App[], categoryId: Category['id']): Criterion[] {
  const ids = new Set<Criterion['id']>()
  const list: Criterion[] = []

  apps.forEach((app) => {
    const acrs = app.categoryRecommendations?.docs?.filter(isAcrObject) || []
    acrs.forEach((acr) => {
      const category = (acr?.category as Category | null) ?? null
      if (!category || category.id !== categoryId) return

      const ccs = category.categoryCriteria?.docs?.filter(isCccObject) || []
      ccs.forEach((ccc) => {
        const crit = ccc?.criterion
        if (!isCriterionObject(crit)) {
          if (process.env.NODE_ENV !== 'production' && crit !== null && crit !== undefined) {
            logger.error(
              '[criteria] Expected populated `ccc.criterion` object but received ID. Increase query depth or add populate for `category.categoryCriteria.criterion`.',
              { appId: app.id, categoryId: category.id, criterion: crit },
            )
          }
          return
        }
        if (!ids.has(crit.id)) {
          ids.add(crit.id)
          list.push(crit)
        }
      })
    })
  })

  return list
}

/**
 * Selects the primary Category across a list of apps by overlap.
 *
 * Strategy:
 * - Count how many apps reference each category via `app.categoryRecommendations.docs`.
 * - Also count how many of those references are "recommended" (i.e., recommendation !== NotRecommended).
 * - Prefer the category with the most recommended apps; fall back to the category with the most total apps;
 *   finally fall back to the first discovered category. Return null if no categories are present.
 *
 * Requirements:
 * - Each App should have populated `categoryRecommendations.docs[].category` as a Category object
 *   (not an ID). If a numeric ID is encountered, it is skipped and a dev-only warning is logged.
 * - Deep population may require high `depth` and/or explicit populate paths for:
 *   categoryRecommendations -> category -> categoryCriteria -> criterion.
 *
 * Notes:
 * - This function is wrapped in React's cache(). It caches by the identity of the `apps` array argument;
 *   pass a stable reference when possible to benefit from memoization.
 *
 * @param {App[]} apps
 *   List of apps, ideally with populated `categoryRecommendations.docs[].category`.
 * @returns {Category | null}
 *   The best matching Category based on overlap and recommendation strength, or null if none can be derived.
 *
 * @example
 * const apps = (stack.stackApps?.docs ?? []).map(doc => doc.app as App)
 * const primaryCategory = extractPrimaryOverlappingCategoryByApps(apps)
 * if (primaryCategory) {
 *   // Use primaryCategory.id, primaryCategory.name, etc.
 * }
 */
export const extractPrimaryOverlappingCategoryByApps = cache((apps: App[]) => {
  let topOverlappingCategory: Category | null = null
  let topOverlappingCategoryRecommendedCategory: Category | null = null
  let topOverlappingAppCount = 0
  let topOverlappingRecommendedAppCount = 0

  const allCategories = apps
    .map((app) => (app.categoryRecommendations?.docs ?? []).filter(isAcrObject))
    .flatMap((docs) =>
      docs
        .filter((doc) => !isDiscontinued(doc))
        .map((doc) => doc?.category as Category | number | null | undefined)
        .filter((cat): cat is Category => {
          const isValid = !!cat && typeof cat !== 'number'
          if (!isValid && process.env.NODE_ENV !== 'production') {
            logger.warn(
              '[extractPrimaryOverlappingCategoryByApps] Skipping invalid category in ACR doc',
              {
                category: cat,
                stackTrace: new Error().stack,
              },
            )
          }
          return isValid
        }),
    )

  if (!allCategories.length) return null

  const categoryMap = new Map(allCategories.map((category) => [category.id, category]))
  const categoryUsageMap = new Map<
    Category['id'],
    {
      apps: Array<App['id']>
      recommendedApps: Array<App['id']>
    }
  >(
    categoryMap.values().map((category) => [
      category.id,
      {
        apps: [],
        recommendedApps: [],
      },
    ]),
  )

  apps.forEach((app) => {
    ;(app.categoryRecommendations?.docs ?? []).filter(isAcrObject).forEach(
      (categoryRecommendation) => {
        if (isDiscontinued(categoryRecommendation)) return
        if (typeof categoryRecommendation.category === 'number') {
          if (process.env.NODE_ENV !== 'production') {
            logger.error('categoryRecommendation.category is a number, ensure populate is set', {
              categoryRecommendation,
              stackTrace: new Error().stack,
            })
          }
          return
        }
        const categoryId = (categoryRecommendation.category as Category).id
        const categoryUsage = categoryUsageMap.get(categoryId)
        if (categoryUsage) {
          categoryUsage.apps.push(app.id)

          if (categoryRecommendation.recommendation !== Recommendation.NotRecommended) {
            categoryUsage.recommendedApps.push(app.id)
          }
        }
      },
    )
  })

  categoryUsageMap.forEach((categoryUsage, categoryId) => {
    const category = categoryMap.get(categoryId)
    if (!category) return

    if (categoryUsage.apps.length > topOverlappingAppCount) {
      topOverlappingAppCount = categoryUsage.apps.length
      topOverlappingCategory = category
    }

    if (categoryUsage.recommendedApps.length > topOverlappingRecommendedAppCount) {
      topOverlappingRecommendedAppCount = categoryUsage.recommendedApps.length
      topOverlappingCategoryRecommendedCategory = category
    }
  })

  const firstCategory = allCategories[0]

  return topOverlappingCategoryRecommendedCategory || topOverlappingCategory || firstCategory
})

export function checkAppBranding(
  app:
    | Pick<
        App,
        | 'id'
        | 'icon'
        | 'slug'
        | 'glyph'
        | 'categoryRecommendations'
        | 'brandBackgroundColor'
        | 'brandPrimaryColor'
        | 'brandSecondaryColor'
        | 'brandTertiaryColor'
        | 'websiteUrl'
        | 'name'
        | 'screenshots'
        | 'homepageImage'
      >
    | undefined
    | null,
): { appIcon: Media | null; appGlyph: Media | null; appHomepage: Media | null } {
  if (!app) {
    return { appIcon: null, appGlyph: null, appHomepage: null }
  }

  const appIcon = isPopulatedObject<Media>(app.icon) ? app.icon : null
  const appGlyph = isPopulatedObject<Media>(app.glyph) ? app.glyph : null
  const appHomepage = isPopulatedObject<Media>(app.homepageImage) ? app.homepageImage : null

  if (!appIcon && !appGlyph) {
    logger.warn(`App "${app.name ?? app.id}" has no populated icon or glyph`)
  }

  return { appIcon, appGlyph, appHomepage }
}

type AffiliateLink = {
  url: string
  passReferrerUrl: boolean
  // True only when the app has a partner tier. Drives rel="sponsored" on the
  // outbound efficient.link/r link — a tracked link for a non-partner app is not sponsored.
  isSponsored: boolean
}

export function processAffiliateLink(
  link: number | Link | null | undefined,
  slug: string | null | undefined,
  appName?: string,
  partnershipTier?: App['partnershipTier'],
): AffiliateLink {
  const isPopulated = typeof link === 'object' && link !== null
  const url = isPopulated ? link.shortLink : slug ? `https://efficient.link/r/${slug}` : `#`

  if (appName && url !== '#') {
    registerAffiliateInfo(url, {
      name: appName,
      epcValue: isPopulated ? link.epcValue : undefined,
    })
  }

  return {
    url,
    passReferrerUrl: isPopulated ? link.passReferrerUrl ?? false : false,
    isSponsored: !!partnershipTier,
  }
}

export function maskEmail(email: string): string {
  const atIndex = email.indexOf('@')
  if (atIndex < 0) return '***'

  const local = email.slice(0, atIndex)
  const domain = email.slice(atIndex + 1)
  const visible = local.slice(0, Math.ceil(local.length / 2))
  return `${visible}***@${domain}`
}

const replaceLabels = {
  ' ai ': ' AI ',
  ' ai.': ' AI.',
  ' crm ': ' CRM ',
  ' crm.': ' CRM.',
  ' url ': ' URL ',
  ' url.': ' URL.',
  ' cms ': ' CMS ',
  ' cms.': ' CMS.',
  ' voip ': ' VoIP ',
  ' voip.': ' VoIP.',
  ' seo ': ' SEO ',
  ' seo.': ' SEO.',
  ' hr ': ' HR ',
  ' hr.': ' HR.',
  ' vpn ': ' VPN ',
  ' vpn.': ' VPN.',
  ' erp ': ' ERP ',
  ' erp.': ' ERP.',
  ' pos ': ' POS ',
  ' pos.': ' POS.',
  ' sso ': ' SSO ',
  ' sso.': ' SSO.',
  ' ocr ': ' OCR ',
  ' ocr.': ' OCR.',
  ' it ': ' IT ',
  ' it.': ' IT.',
  ' bi ': ' BI ',
  ' bi.': ' BI.',
  ' ui ': ' UI ',
  ' ui.': ' UI.',
  ' ux ': ' UX ',
  ' ux.': ' UX.',
} as const

export function replaceTitleWordCasing(title: string): string {
  const padded = ` ${title} `
  return Object.entries(replaceLabels)
    .reduce((acc, [key, value]) => {
      return acc.replace(key, value)
    }, padded)
    .trim()
}
